// src/hooks/useTesisatRebuild.js
//
// JBZ load sonrasi (veya runtime) tesisatSlice'taki kayitlari sahneye yeniden
// insa eder. Util fonksiyonlari src/utils/tesisat/ altinda.
//
// Pattern: Redux veri katmani -> factory -> mesh.

import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import * as THREE from 'three';
import { selectAllTesisat } from '../../../redux/tesisatSlice';
import { resolveWorldPosition } from '../../../shared/coordinates/floorPlanOrigin';
import { updatePanelExitPoints } from '../../../modules/wiring';
import { buildTesisatMesh } from '../../../modules/fpSync';
import { findInScene, computeAnchoredWorld, disposeMesh } from '../../../modules/fpSync';
import { RESOLVED_TO_LEGACY, LOCAL_BUG_TYPES, toFpMeshLocal } from '../../../modules/fpSync';
import { useFloorPlanShiftCorrection } from '../../../modules/fpSync';
import { isTesisatMesh } from '../../../modules/fpSync';
import { shouldSkipForSnapshot } from '../sceneSnapshotMode';

// Anchor-based repositioning'den haric tutulan tipler
const ANCHORED_EXCLUDED = new Set([
  'electricalPanel', 'mainPanel', 'supplyPoint', 'cableShaft',
]);

// Bu hook'un YÖNETTİĞİ mesh mi? (tesisat — wire ve doküman/DXF içeriği HARİÇ).
// KRİTİK: builtRef'e yalnızca tesisat girmeli ve cleanup yalnızca tesisat
// silmeli. Aksi halde snapshot-skip "userData.id'li her şeyi" yakalıyor
// (DXF doküman içeriği dahil) ve cleanup bunları "Redux'tan silinmiş tesisat"
// sanıp yok ediyordu → priz eklenince Topraklama/Kat Planı/Sembol vb. dokümanlar
// boşalıyordu. wire'lar useWireRebuild'in sorumluluğunda, burada hariç.
function isManagedTesisat(o) {
  const t = o?.userData?.type;
  if (typeof t === 'string' && t.startsWith('wire')) return false;
  if (o?.userData?.role === 'wireMarker') return false;
  // Gerçek wire'lar (id wire/feeder) useWireRebuild'in sorumluluğunda. isTesisatMesh
  // artık onları da kapsıyor (fpMesh sweep için); cleanup'ın dispose etmesini
  // engellemek için burada AÇIKÇA hariç tut.
  const id = o?.userData?.id;
  if (typeof id === 'string' && /^(wire|feeder)/i.test(id)) return false;
  return isTesisatMesh(o);
}

function buildFpMeshMap(scene) {
  const map = new Map();
  scene.traverse(o => {
    if (o.userData?.isFloorPlanMesh && o.userData?.floorPlanId) {
      map.set(o.userData.floorPlanId, o);
    }
  });
  return map;
}

function buildTabFpMapping(tabFpAllIds, tabFpById) {
  const tabFpIdSet = new Set();
  const sourceToTabFpId = {};
  if (tabFpAllIds && tabFpById) {
    for (const fpId of tabFpAllIds) {
      tabFpIdSet.add(fpId);
      const plan = tabFpById[fpId];
      if (plan?.sourceFloorPlanId) {
        tabFpIdSet.add(plan.sourceFloorPlanId);
        sourceToTabFpId[plan.sourceFloorPlanId] = fpId;
      }
    }
  }
  return { tabFpIdSet, sourceToTabFpId };
}

function resolveFpMesh(item, fpMeshMap, sourceToTabFpId) {
  if (item.floorPlanId) {
    const resolvedFpId = sourceToTabFpId[item.floorPlanId] || item.floorPlanId;
    return fpMeshMap.get(resolvedFpId) || null;
  }
  if (fpMeshMap.size > 0) return fpMeshMap.values().next().value;
  return null;
}

function handleExistingMesh(existing, item, fpMeshMap, sourceToTabFpId, scene) {
  // _v:3: fpMesh-LOCAL — fpMesh'e ekle
  if (item._v === 3 && item.floorPlanId) {
    const fpMesh = resolveFpMesh(item, fpMeshMap, sourceToTabFpId);
    if (fpMesh && existing.parent !== fpMesh) {
      fpMesh.add(existing);
      existing.position.set(item.position?.x ?? 0, item.position?.y ?? 0, item.position?.z ?? 0);
    }
    return;
  }
  const isOldFmt = !item._v && item.position;
  if (isOldFmt && LOCAL_BUG_TYPES.includes(item.type)) {
    const fpMesh = resolveFpMesh(item, fpMeshMap, sourceToTabFpId);
    if (fpMesh && existing.parent !== fpMesh) {
      const localPos = toFpMeshLocal(item.position, fpMesh);
      fpMesh.add(existing);
      existing.position.set(localPos.x, localPos.y, 0.5);
    }
  } else {
    const worldPos = resolveWorldPosition(item);
    if (worldPos && item.localPosition && item.floorPlanId) {
      const t = item.type;
      const isPanelLike = t === 'mainPanel' || t === 'electricalPanel'
        || t === 'supplyPoint' || t === 'supplyBox';
      const isOldFormat = !item._v && item.floorPlanId;
      // Mesh-FP invariant: sahnedeki mesh fpMesh-child ise konumu DOĞRU kabul
      // edilir. Editor tip değişimi gibi tam rebuild senaryosunda rescue +
      // sweep tesisat'ı _savedLocalPos ile yeni fpMesh'in doğru local pos'una
      // attach ediyor. Bu noktada Redux'taki worldPos (item.position) eski
      // layout'tan kalan STALE değer olabilir; override edersek tesisat kat
      // planından kopar (armatür/priz/makine eski yerde kalır bug'ı).
      const isInFpMesh = existing.parent?.userData?.isFloorPlanMesh === true;
      if (!isPanelLike && !isOldFormat && !isInFpMesh) {
        const localPos = new THREE.Vector3(worldPos.x ?? 0, worldPos.y ?? 0, worldPos.z ?? 0);
        if (existing.parent && existing.parent !== scene) {
          existing.parent.updateWorldMatrix(true, false);
          existing.parent.worldToLocal(localPos);
        }
        existing.position.set(localPos.x, localPos.y, localPos.z);
      }
      existing.userData = {
        ...existing.userData,
        localPosition: item.localPosition,
        floorPlanId: item.floorPlanId,
      };
    }
  }
}

export default function useTesisatRebuild(scenesRef, tabId) {
  const items = useSelector(selectAllTesisat);
  const legacyTabId = RESOLVED_TO_LEGACY[tabId] || tabId;
  const tabFpAllIds = useSelector(state => state.floorPlans.tabs?.[legacyTabId]?.allIds);
  const tabFpById = useSelector(state => state.floorPlans.tabs?.[legacyTabId]?.byId);
  const components = useSelector(state => state.simpleSupply.components);
  const layoutCounter = useSelector(state => state.utils?.updateCounter ?? 0);
  const componentsRef = useRef([]);
  componentsRef.current = components;
  const builtRef = useRef(new Map());
  const lastSnapshotSkippedRef = useRef(0);

  useEffect(() => { builtRef.current = new Map(); }, [tabId]);

  // FP shift correction (ayri hook)
  useFloorPlanShiftCorrection(scenesRef, tabId, builtRef);

  // ── Main rebuild effect ──────────────────────────────────────────────────
  useEffect(() => {
    const scene = scenesRef?.current?.[tabId];
    if (!scene) return;

    // [FAZ 2 — save-load-rewrite] Snapshot mode guard
    // Restore akışından sonra ilk tetiklenmede skip — scene tam yüklü, rebuild
    // duplicate yaratır. Drawing-time'da normal çalışır.
    if (shouldSkipForSnapshot(lastSnapshotSkippedRef)) {
      // Snapshot'tan gelen TESİSAT mesh'lerini builtRef'e bağla (sonraki
      // tetiklenmelerde existing kontrolü çalışsın diye). SADECE tesisat —
      // doküman/DXF içeriğini yakalarsak cleanup onları siler (bkz. isManagedTesisat).
      scene.traverse(o => {
        if (o.userData?.id && isManagedTesisat(o)) builtRef.current.set(o.userData.id, o);
      });
      return;
    }

    const { tabFpIdSet, sourceToTabFpId } = buildTabFpMapping(tabFpAllIds, tabFpById);
    const fpMeshMap = buildFpMeshMap(scene);
    const currentIds = new Set();

    for (const item of items) {
      if (!item?.id) continue;
      if (item.floorPlanId && tabFpIdSet.size > 0 && !tabFpIdSet.has(item.floorPlanId)) continue;
      currentIds.add(item.id);

      const existing = builtRef.current.get(item.id) || findInScene(scene, item.id);
      if (existing) {
        // Snapshot pencere içinde olmamamız demek drawing-time'dayız demek
        // (guard yukarıda zaten skip ediyor). Drawing-time'da Redux update
        // varsa mesh pos override gerekiyor olabilir.
        handleExistingMesh(existing, item, fpMeshMap, sourceToTabFpId, scene);
        builtRef.current.set(item.id, existing);
        continue;
      }

      const mesh = buildTesisatMesh(item);
      if (!mesh) continue;

      if (item._v === 3 && item.floorPlanId) {
        // _v:3: position fpMesh-LOCAL — dogrudan fpMesh'e ekle
        const fpMesh = resolveFpMesh(item, fpMeshMap, sourceToTabFpId);
        if (fpMesh) {
          fpMesh.add(mesh);
        } else {
          continue; // fpMesh henuz yok — sonraki run'da tekrar denenecek
        }
      } else {
        if (!item._v && item.floorPlanId && item.position && LOCAL_BUG_TYPES.includes(item.type)) {
          mesh.userData._savedLocalPos = {
            x: item.position.x ?? 0,
            y: item.position.y ?? 0,
            z: item.position.z ?? 0,
          };
        }
        scene.add(mesh);
      }
      builtRef.current.set(item.id, mesh);
    }

    // Drawing-time: Anchor-based repositioning (snapshot pencere içinde
    // değiliz, guard yukarıda zaten skip etti restore senaryosunda).
    for (const [id, mesh] of builtRef.current.entries()) {
      if (!mesh) continue;
      const item = items.find(i => i.id === id);
      if (!item?.anchor?.entityId || ANCHORED_EXCLUDED.has(item.type)) continue;
      const wp = computeAnchoredWorld(scene, item);
      if (wp) mesh.position.set(wp.x, wp.y, wp.z);
    }

    // Drawing-time: Panel exit-points oluştur (yeşil daireler + geometry).
    // skipWiringPositions=true → wire ilk-iki vertex'i default'a çekme.
    for (const [id, mesh] of builtRef.current.entries()) {
      if (!mesh) continue;
      const item = items.find(i => i.id === id);
      if (!item) continue;
      if (item.type !== 'electricalPanel' && item.type !== 'mainPanel') continue;
      const component = componentsRef.current.find(c => c.id === item.id);
      const exitCount = component?.outgoingFeeders?.length || 0;
      if (exitCount <= 0) continue;
      try { updatePanelExitPoints(mesh, exitCount, scene, null, true); }
      catch (err) { console.warn('[tesisat-rebuild] panel exit failed:', item.id, err); }
    }

    // Redux'tan silinenleri sahneden cikar — AMA yalnızca gerçekten tesisat
    // olan mesh'leri. builtRef'e (geçmiş hatalardan veya başka yollardan)
    // tesisat-olmayan bir nesne sızsa bile dispose ETME; sadece referansı bırak.
    for (const [id, mesh] of Array.from(builtRef.current.entries())) {
      if (!currentIds.has(id)) {
        if (isManagedTesisat(mesh)) disposeMesh(mesh);
        builtRef.current.delete(id);
      }
    }

  }, [items, tabId, tabFpAllIds, tabFpById, layoutCounter, scenesRef]);
}
